import Typography from "@/design-system/Typography";
import TelemetryButton from "@/design-system/telemetry-button";
import { languageSelector, useViewportInfoContext } from "@/src/contexts";
import { FlyersTabBrandsItemResources } from "@/src/flyers/FlyersTabBrandsItem.Resources";
import { FlyersTabBrandsItemResourcesEn } from "@/src/flyers/FlyersTabBrandsItem.Resources.En";
import type { IComponent } from "@Components/Component";
import { defaultAttributes } from "@Components/Component";
import type { IImageLoader } from "@Components/ImageLoader";
import ImageLoader from "@Components/ImageLoader";
import * as stylex from "@stylexjs/stylex";

import { colors, genericStyles } from "../../styles/tokens.stylex";
import LogoTile from "../common/LogoTile";
import FavoriteLink, { ClickFromType } from "../common/links/FavoriteLink";

import * as css from "./FlyersTabBrandsItem.module.css";

export interface IFlyersTabBrandsItem extends IComponent {
    image: IImageLoader;
    flyersCountTitle: string;
    newFlyerTitle?: string;
    navigationUrl: string;
    isNewFlyer: boolean;
    groupKey: string;
    brandId: string;
}

export default function FlyersTabBrandsItem(props: IFlyersTabBrandsItem) {
    const { isMobile } = useViewportInfoContext();

    const resources = languageSelector(FlyersTabBrandsItemResources, FlyersTabBrandsItemResourcesEn);

    return (
        <div {...defaultAttributes(props, css.flyersTabBrandsItemComponent)}>
            {isMobile ? (
                <div {...stylex.props(styles.table)}>
                    <div {...stylex.props(styles.details)}>
                        <TelemetryButton
                            as="a"
                            href={props.navigationUrl}
                            variant="none"
                            size="M"
                            styleX={styles.link}
                            eventName="BrandDetails-brandClick"
                            itemId={props.brandId}
                            param1={props.isNewFlyer ? "NewFlyer" : null}
                            origin="FlyersWorld"
                        >
                            <>
                                <div {...stylex.props(styles.image)}>
                                    <ImageLoader {...props.image} />
                                </div>
                                <div {...stylex.props(styles.description)}>
                                    <Typography
                                        textElement="h3"
                                        fontStyle="BodyL"
                                        styleX={styles.descriptionTitle}
                                    >
                                        {props.flyersCountTitle}
                                    </Typography>
                                    {props.isNewFlyer && (
                                        <Typography
                                            textElement="span"
                                            fontStyle="BodyS"
                                            styleX={[styles.newFlyer, styles.border]}
                                        >
                                            {resources.newFlyerText}
                                        </Typography>
                                    )}
                                </div>
                            </>
                        </TelemetryButton>
                    </div>
                    <div {...stylex.props(styles.follow)}>
                        <FavoriteLink
                            favoriteLinkIconSize="border"
                            showText={false}
                            type="brands"
                            id={props.brandId}
                            origin={ClickFromType.FlyersWorld}
                            className={stylex.props(styles.favoriteLink).className}
                        />
                    </div>
                </div>
            ) : (
                <LogoTile
                    id={props.brandId}
                    logo={props.image}
                    href={props.navigationUrl}
                    telemetryCustom={{ eventName: "Brand-brandClick", actionType: "Undefined" }}
                    type="brands"
                    newFlyerText={props.isNewFlyer ? resources.newFlyerText : null}
                />
            )}
        </div>
    );
}

const styles = stylex.create({
    newFlyer: {
        paddingBlock: "0",
        paddingInline: "10px",
        backgroundColor: colors.fg_Success,
        color: "white",
        textTransform: "uppercase",
        marginTop: 3,
    },

    image: {
        display: "table-cell",

        textAlign: "center",
        verticalAlign: "middle",
        width: 50,
    },

    description: {
        padding: 20,
        display: "table-cell",
        verticalAlign: "middle",
    },

    descriptionTitle: {
        margin: 0,
        color: "black",
    },

    border: {
        borderRadius: genericStyles.borderRadiusS1,
    },

    table: {
        display: "table",
        tableLayout: "fixed",
        height: 80,
        width: "100%",
    },

    link: {
        display: "table",
        tableLayout: "fixed",
        width: "100%",
    },

    details: {
        display: "table-cell",
        verticalAlign: "middle",
    },

    follow: {
        display: "table-cell",
        verticalAlign: "middle",
        width: 35,
    },

    favoriteLink: {
        marginBlock: "0",
        marginInline: "auto",
    },
});
